import React, { useMemo, useState } from 'react';
import {
  Box,
  costFormater,
  getFullnameAbbr,
  List,
  Option,
  OptionKey,
  OverflowText,
  Text,
} from '@common-fe/common-fe';

import { LoadMoreButton } from '@/modules/claims/ClaimsProcessor/ClaimsDetails/SpendLimitPerDependent/components/LoadMoreButton';
import { DEPENDERS_COLORS } from '@/modules/deductibles/SpendTracker/SpendTrackerUser';
import { EmptyTransactionsListPlaceholder } from '@/modules/transaction/TransactionsList/EmptyTransactionsListPlaceholder';
import theme from '@/styles/theme';

import { SpendLimitsPeriodHeader } from './SpendLimitsPeriodHeader';
import type { DependentSpendLimit } from './types';

interface SpendLimitsTableProps {
  dependents: DependentSpendLimit[];
  isLoading?: boolean;
  showPeriodDropdown?: boolean;
  periodOptions?: Option[];
  selectedPeriod?: OptionKey;
  onPeriodChange?: (value: OptionKey) => void;
  isPeriodLoading?: boolean;
}
const INITIAL_ITEMS_COUNT = 5;

export const SpendLimitsTable: React.FC<SpendLimitsTableProps> = ({
  dependents,
  isLoading,
  showPeriodDropdown,
  periodOptions = [],
  selectedPeriod,
  onPeriodChange,
  isPeriodLoading,
}) => {
  const [showAll, setShowAll] = useState(false);
  const hasData = useMemo(() => dependents.length > 0, [dependents.length]);
  const hasMoreItems = dependents.length > INITIAL_ITEMS_COUNT;
  const displayedDependents = useMemo(
    () => (showAll || !hasMoreItems ? dependents : dependents.slice(0, INITIAL_ITEMS_COUNT)),
    [dependents, showAll, hasMoreItems]
  );
  const remainingCount = dependents.length - INITIAL_ITEMS_COUNT;

  return (
    <Box data-testid="SpendLimitsTable" background="module" round="container1Round" pad="l">
      <SpendLimitsPeriodHeader
        title={`Person spend limits: ${dependents?.length || '0'}`}
        showPeriodDropdown={showPeriodDropdown}
        periodOptions={periodOptions}
        selectedPeriod={selectedPeriod}
        onPeriodChange={onPeriodChange}
        isPeriodLoading={isPeriodLoading}
        testIdPrefix="spend-limits"
      />

      <List>
        <List.Header style={{ border: `1px solid ${theme.colors.legend}` }}>
          <List.Header.Item flex={5}>Full name</List.Header.Item>
          <List.Header.Item flex={2.5} margin={{ left: 's' }}>
            Person spend limit
          </List.Header.Item>
          <List.Header.Item flex={2.5}>Total spent</List.Header.Item>
        </List.Header>

        {isLoading ? (
          <List.Preloader testId="spend-limits-preloader" count={1} />
        ) : (
          <>
            <Box style={{ position: 'relative' }}>
              {!hasData && <EmptyTransactionsListPlaceholder title="Spend Limits" />}
              {displayedDependents.map((dependent, index) => {
                const isLastItem = index === displayedDependents.length - 1;
                const shouldShowLoader = isLastItem && showAll && isLoading;

                return (
                  <React.Fragment key={dependent.id}>
                    <List.Item disableHover data-testid={`spend-limit-item-${dependent.id}`}>
                      <Box style={{ flex: 5 }} direction="row" align="center" gap="spacing12">
                        <Box
                          width="32px"
                          height="32px"
                          align="center"
                          round="50%"
                          justify="center"
                          style={{ minWidth: '32px' }}
                          background={DEPENDERS_COLORS[index % DEPENDERS_COLORS.length]}
                        >
                          <Text color="iconOnColor" weight={700}>
                            {getFullnameAbbr(dependent.name)}
                          </Text>
                        </Box>
                        <Box direction="row" align="center" gap="spacing8">
                          <OverflowText title={dependent.name} textStyle={{ fontWeight: 'bold' }} />
                          {dependent.relationshipLabel && (
                            <Text weight={400} color="textSecondary">
                              ({dependent.relationshipLabel})
                            </Text>
                          )}
                        </Box>
                      </Box>
                      <List.Item.Text flex={2.5} weight={400} style={{ marginLeft: theme.spacings.s }}>
                        {costFormater(dependent.personSpendLimit, true)}
                      </List.Item.Text>
                      <List.Item.Text flex={2.5} weight={400}>
                        {costFormater(dependent.totalSpent, true)}
                      </List.Item.Text>
                    </List.Item>
                    {shouldShowLoader && (
                      <Box align="center" pad={{ vertical: 'spacing8' }}>
                        <List.Preloader testId="spend-limits-last-item-loader" count={1} />
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
              {hasMoreItems && !showAll && (
                <Box
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '120px',
                    background: theme.colors.fade1,
                  }}
                />
              )}
            </Box>
            {hasMoreItems && (
              <Box pad={{ vertical: 'spacing16' }}>
                <LoadMoreButton
                  isExpanded={showAll}
                  remainingCount={remainingCount}
                  onClick={() => setShowAll(!showAll)}
                  isPending={isLoading}
                />
              </Box>
            )}
          </>
        )}
      </List>
    </Box>
  );
};
